import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi, useNavigate, useRouterState } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { LOAN_REQUEST_USAGES } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuSave } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { useUpdateOfferRequestConditionsMutation } from './hooks/useUpdateOfferRequestConditionsMutation';
import { NewCreditRequest } from './NewCreditRequest';
import { useOfferRequestConditionsLatest } from './NewCreditRequest/hooks/useOfferRequestConditionsLatest';
import { CreditRequestHeaderActions } from './NewCreditRequest/RequestConfiguration/CreditRequestHeaderActions';
import { RouteOfferExtraInfoDialog } from './NewCreditRequest/RequestConfiguration/OfferExtraInfo/RouteOfferExtraInfoDialog';
import type { RequestHeaderKind } from './NewCreditRequest/RequestConfiguration/types';
import {
    getRequestHeaderKindFromPathname,
    REQUEST_HEADER_KINDS,
    REQUEST_HEADER_ROUTES,
} from './NewCreditRequest/RequestConfiguration/types';
import { useBankCreditRequestFormContext } from '../../context/BankCreditRequestFormContext';
import { getRuntimeOptions } from './NewCreditRequest/RequestConfiguration/util/getRuntimeOptions';
import type { SchemaCreditRequestForm } from './NewCreditRequest/schemas/creditRequestForm.schema';
import { schemaCreditRequestForm } from './NewCreditRequest/schemas/creditRequestForm.schema';
import { getSalaryFromLead } from './NewCreditRequest/utils/getSalaryFromLead';
import { prepareDefaultValues } from './NewCreditRequest/utils/prepareDefaultValues';
import { prepareSelectedRuntimeOption } from './utils/prepareSelectedRuntimeOption';
import useLead from '../../hooks/useLead';
import { OfferHistory } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import {
    clearLeadDetailsOverlaySearch,
    updateLeadDetailsOverlaySearch,
} from '~/features/leadDetails/utils/normalizeLeadDetailsSearch';
import { useOnMountAndChange } from '~/hooks/useOnMountAndChange';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const leadDetailsRoute = getRouteApi('/_app/leads_/details/$leadId');

const BankCreditRequestContent: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const { isDirty, registerDirtyState, registerReset, registerSubmit, trigger } = useBankCreditRequestFormContext();

    const [currentRequestId, setCurrentRequestId] = useState<string>();
    const navigate = useNavigate({ from: '/leads/details/$leadId/bank-request' });
    const contentType = useRouterState({
        select: ({ location }) => getRequestHeaderKindFromPathname(location.pathname),
    });
    const { offerExtraInfoId } = leadDetailsRoute.useSearch();

    const { data: offerRequestConditionsLatest, isError, isLoading } = useOfferRequestConditionsLatest(lead.id);
    const salaryValues = useMemo(() => getSalaryFromLead(lead), [lead]);

    const methods = useForm<SchemaCreditRequestForm>({
        defaultValues: prepareDefaultValues({
            lead,
            requestConditions: offerRequestConditionsLatest,
            salaryValues,
        }),
        resolver: zodResolver(schemaCreditRequestForm),
    });

    const { getValues, handleSubmit, reset, resetField, setValue } = methods;

    const valueVerwendungszweck = useWatch({ control: methods.control, name: 'verwendungszweck' });
    const valueCarType = useWatch({ control: methods.control, name: 'carFinance.vehicleType' });
    const valueCarFinancingWish = useWatch({ control: methods.control, name: 'carFinance.financingWish' });

    // NOTE: this memo is necessary to avoid unnecessary re-renders for the runtime options
    const runtimeOptions = useMemo(
        () =>
            getRuntimeOptions({
                carFinancingWish: valueCarFinancingWish,
                carType: valueCarType,
                usageType: valueVerwendungszweck,
            }),
        [valueCarType, valueCarFinancingWish, valueVerwendungszweck],
    );

    const { mutateAsync: updateOfferRequestConditions } = useUpdateOfferRequestConditionsMutation(lead);

    const closeOfferExtraInfo = useCallback(() => {
        navigate({
            replace: true,
            search: (previous) =>
                updateLeadDetailsOverlaySearch(previous, LEAD_DETAILS_OVERLAYS.OFFER_EXTRA_INFO_ID, undefined),
        });
    }, [navigate]);

    const changeContentType = (nextContentType: RequestHeaderKind) => {
        if (nextContentType === contentType) return;

        navigate({
            params: { leadId: lead.id },
            search: clearLeadDetailsOverlaySearch,
            to: REQUEST_HEADER_ROUTES[nextContentType],
        });
    };

    // this will be used to ensure that the runtime option is always selectable when the page is loaded
    // NOTE: useLayoutEffect + setValue so the runtime updates before paint without touching the rest of the form's dirty state
    useLayoutEffect(() => {
        const currentValues = getValues();

        const selectedRuntimeOption = prepareSelectedRuntimeOption({
            carFinancingWish: currentValues.carFinance?.financingWish,
            carType: currentValues.carFinance?.vehicleType,
            currentRuntime: currentValues.kreditLaufzeit,
            usageType: currentValues.verwendungszweck,
        });
        if (selectedRuntimeOption !== -1) {
            setValue('kreditLaufzeit', selectedRuntimeOption, { shouldDirty: true, shouldTouch: true });
        }
    }, [getValues, setValue, valueCarType, valueCarFinancingWish, valueVerwendungszweck]);

    useEffect(() => {
        registerDirtyState({
            dirty: methods.formState.isDirty,
            dirtyFields: methods.formState.dirtyFields,
        });
    }, [registerDirtyState, methods.formState]);

    useEffect(() => {
        registerReset(() => {
            reset();
        });
    }, [registerReset, reset]);

    useOnMountAndChange(() => {
        registerSubmit((setSubmitStatus) =>
            handleSubmit(
                async (data) => {
                    if (isDirty()) {
                        if (data.verwendungszweck !== LOAN_REQUEST_USAGES.FAHRZEUGKAUF && data.carFinance) {
                            resetField('carFinance', { defaultValue: {} });
                        }
                        await updateOfferRequestConditions({ getValues, setSubmitStatus });
                        const newDefaultValues = getValues();

                        const selectedRuntimeOption = prepareSelectedRuntimeOption({
                            carFinancingWish: newDefaultValues.carFinance?.financingWish,
                            carType: newDefaultValues.carFinance?.vehicleType,
                            currentRuntime: newDefaultValues.kreditLaufzeit,
                            usageType: newDefaultValues.verwendungszweck,
                        });
                        if (selectedRuntimeOption !== -1) {
                            newDefaultValues.kreditLaufzeit = selectedRuntimeOption;
                        }

                        reset(newDefaultValues);
                    } else {
                        setSubmitStatus({ saved: false });
                        toast.info(t('BANK_CREDIT_REQUEST.NO_CHANGES_TO_SAVE'));
                    }
                },
                (error: unknown) => {
                    console.error(error);
                    setSubmitStatus({ error, saved: false });
                },
            ),
        );
    }, [lead, handleSubmit, reset, resetField, getValues, registerSubmit, t]);

    return (
        <FormProvider {...methods}>
            {offerExtraInfoId && (
                <RouteOfferExtraInfoDialog
                    key={offerExtraInfoId}
                    leadId={lead.id}
                    offerId={offerExtraInfoId}
                    onClose={closeOfferExtraInfo}
                />
            )}
            <div className="py-2 bg-grey-light @container relative">
                <div className="flex justify-between py-2">
                    <div className="space-x-2 p-1 w-full">
                        <h6 className="text-midnight font-medium">{t('BANK_CREDIT_REQUEST.TITLE')}</h6>
                    </div>
                    <Button type="button" onClick={trigger}>
                        <LuSave className="w-4 h-4" />
                        {t('BUTTONS.SAVE')}
                    </Button>
                </div>
                <CreditRequestHeaderActions contentType={contentType} onContentTypeChange={changeContentType} />

                <div className="py-2 bg-grey-light">
                    {contentType === REQUEST_HEADER_KINDS.NEW_CREDIT_REQUEST && (
                        <NewCreditRequest
                            isLoading={isLoading}
                            isError={isError}
                            lead={lead}
                            requestConditions={offerRequestConditionsLatest}
                            currentRequestId={currentRequestId}
                            setCurrentRequestId={setCurrentRequestId}
                            runtimeOptions={runtimeOptions}
                        />
                    )}
                    {contentType === REQUEST_HEADER_KINDS.REQUEST_HISTORY && <OfferHistory lead={lead} />}
                </div>
            </div>
        </FormProvider>
    );
};

export const BankCreditRequestSection: React.FC = () => {
    const { leadId } = leadDetailsRoute.useParams();
    const { data: lead } = useLead(leadId);

    if (!lead) return null;

    return <BankCreditRequestContent key={lead.id} lead={lead} />;
};
